import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  X, GripVertical, Trash2, Copy, Plus, ArrowUp, ArrowDown, ExternalLink,
  Eye, Save, Loader2, LayoutGrid, Settings as SettingsIcon, Share2, ChevronLeft, Pencil,
  GitBranch, ImagePlus, Check,
} from "lucide-react";
import { Button } from "../ui/button";
import { formsApi, pipelineApi } from "../../lib/api";
import { toast } from "sonner";
import {
  FIELD_DEFS, makeField, normalizeField, FieldControl, FieldDisplayBlock, DISPLAY_ONLY_TYPES,
  MAP_OPTIONS, FIXED_MAP_TYPES, NO_PLACEHOLDER_TYPES,
  THEMES, COND_OPS, headerStyle, fieldVisible,
} from "./formFields";
import "./form-builder.css";

// Fixed id of the Service Guru Pro marketing site's own Contact Us form,
// seeded once into the platform workspace (see backend services/platform_crm.py).
const PLATFORM_CONTACT_FORM_ID = "form-platform-contact";

/* ---------------- Left palette ---------------- */
function Palette({ dragRef, onAdd, onDragEnd }) {
  return (
    <div className="w-60 shrink-0 bg-slate-800 text-slate-200 flex flex-col" data-testid="builder-palette">
      <div className="px-4 py-3.5 border-b border-slate-700">
        <div className="text-[13px] font-semibold text-white flex items-center gap-2"><LayoutGrid className="h-4 w-4" /> Form Elements</div>
      </div>
      <div className="px-4 pt-3 pb-1 text-[10.5px] font-semibold tracking-wide text-slate-400">BASIC</div>
      <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-0.5">
        {FIELD_DEFS.map((d) => (
          <button
            key={d.type}
            draggable
            onDragStart={(e) => { dragRef.current = { kind: "new", type: d.type }; e.dataTransfer.effectAllowed = "copy"; e.dataTransfer.setData("text/plain", d.type); }}
            onDragEnd={onDragEnd}
            onClick={() => onAdd(d.type)}
            data-testid={`palette-item-${d.type}`}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] text-slate-200 hover:bg-slate-700 active:bg-slate-600 cursor-grab transition-colors text-left"
          >
            <span className="h-7 w-7 rounded bg-slate-700/70 flex items-center justify-center shrink-0"><d.icon className="h-4 w-4 text-slate-300" /></span>
            {d.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Canvas field ---------------- */
function CanvasField({ field, selected, idx, total, accent, inputTextColor, onSelect, onDelete, onDup, onMove, onFieldDragStart, onDragEnd }) {
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  return (
    <div
      data-testid={`canvas-field-${field.id}`}
      onClick={stop(() => onSelect(field.id))}
      className={`relative group rounded-lg px-4 py-3 cursor-pointer transition-all border ${selected ? "border-blue-400 ring-2 ring-blue-100 bg-blue-50/40" : "border-transparent hover:bg-slate-50"}`}
    >
      <div
        draggable
        onDragStart={(e) => { onFieldDragStart(idx); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", "move"); }}
        onDragEnd={onDragEnd}
        onClick={(e) => e.stopPropagation()}
        className="absolute left-0.5 top-1/2 -translate-y-1/2 h-7 w-5 flex items-center justify-center cursor-grab opacity-0 group-hover:opacity-100 text-slate-300 hover:text-slate-500"
        title="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" color="#ffffff" style={{ color: "#ffffff" }} />
      </div>

      <div className="absolute right-2 top-2 flex gap-0.5 opacity-0 group-hover:opacity-100">
        <button onClick={stop(() => onMove(idx, -1))} disabled={idx === 0} data-testid={`field-up-${field.id}`} className="h-6 w-6 rounded bg-blue-600 ring-1 ring-white/40 shadow-md flex items-center justify-center text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"><ArrowUp className="h-3.5 w-3.5 shrink-0" color="#ffffff" style={{ color: "#ffffff" }} /></button>
        <button onClick={stop(() => onMove(idx, 1))} disabled={idx === total - 1} data-testid={`field-down-${field.id}`} className="h-6 w-6 rounded bg-blue-600 ring-1 ring-white/40 shadow-md flex items-center justify-center text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"><ArrowDown className="h-3.5 w-3.5 shrink-0" color="#ffffff" style={{ color: "#ffffff" }} /></button>
        <button onClick={stop(() => onDup(idx))} data-testid={`field-dup-${field.id}`} className="h-6 w-6 rounded bg-blue-600 ring-1 ring-white/40 shadow-md flex items-center justify-center text-white hover:bg-blue-700"><Copy className="h-3.5 w-3.5 shrink-0" color="#ffffff" style={{ color: "#ffffff" }} /></button>
        <button onClick={stop(() => onDelete(field.id))} data-testid={`field-delete-${field.id}`} className="h-6 w-6 rounded bg-rose-600 ring-1 ring-white/40 shadow-md flex items-center justify-center hover:bg-rose-700"><Trash2 className="h-3.5 w-3.5 shrink-0" color="#ffffff" style={{ color: "#ffffff" }} /></button>
      </div>

      {DISPLAY_ONLY_TYPES.has(field.type) ? (
        <div className="pr-24"><FieldDisplayBlock field={field} accent={accent} /></div>
      ) : (
        <>
          <label className="flex items-center gap-1.5 text-[12.5px] font-medium text-slate-700 mb-1.5 pr-24">
            {field.label}{field.required && <span className="text-red-500">*</span>}
            {field.condition?.fieldId && <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded"><GitBranch className="h-3 w-3" /> Conditional</span>}
          </label>
          <FieldControl field={field} disabled accent={accent} inputTextColor={inputTextColor} />
        </>
      )}
    </div>
  );
}

const DropBar = () => <div className="h-1.5 rounded-full bg-blue-500 mx-2 my-0.5" data-testid="canvas-dropbar" />;

/* ---------------- Properties panel ---------------- */
function FieldProperties({ field, allFields, onChange, onDelete }) {
  const showPlaceholder = !NO_PLACEHOLDER_TYPES.has(field.type);
  const showMap = !FIXED_MAP_TYPES.has(field.type) && !DISPLAY_ONLY_TYPES.has(field.type);
  const sources = (allFields || []).filter((f) => f.id !== field.id && f.type !== "heading");
  const cond = field.condition;
  const srcField = cond ? sources.find((f) => f.id === cond.fieldId) : null;

  const toggleCond = (on) => {
    if (on) onChange({ ...field, condition: { fieldId: sources[0]?.id || "", op: "equals", value: "" } });
    else onChange({ ...field, condition: null });
  };
  const setCond = (patch) => onChange({ ...field, condition: { ...cond, ...patch } });
  const needsValue = cond && cond.op !== "filled" && cond.op !== "not_filled";
  return (
    <div className="space-y-4" data-testid="props-field">
      <div className="text-[11px] font-semibold tracking-wide text-slate-400 uppercase">Field · {field.type}</div>
      {field.type === "textBlock" ? (
        <div>
          <label className="text-[12px] font-medium text-slate-600">Content</label>
          <textarea value={field.content || ""} onChange={(e) => onChange({ ...field, content: e.target.value })} data-testid="prop-content" rows={5}
            className="mt-1 w-full px-3 py-2 rounded-md border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
        </div>
      ) : (
        <div>
          <label className="text-[12px] font-medium text-slate-600">{field.type === "heading" ? "Heading text" : field.type === "hyperlink" ? "Link text" : "Label"}</label>
          <input value={field.label} onChange={(e) => onChange({ ...field, label: e.target.value })} data-testid="prop-label"
            className="mt-1 w-full h-9 px-3 rounded-md border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
        </div>
      )}
      {field.type === "textBlock" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[12px] font-medium text-slate-600">Font size</label>
            <select value={field.fontSize || 14} onChange={(e) => onChange({ ...field, fontSize: parseInt(e.target.value, 10) })} data-testid="prop-font-size"
              className="mt-1 w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px] bg-white focus:outline-none">
              {[12, 14, 16, 18, 20, 24, 28, 32].map((s) => <option key={s} value={s}>{s}px</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] font-medium text-slate-600">Text color</label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" value={field.color || "#334155"} onChange={(e) => onChange({ ...field, color: e.target.value })} data-testid="prop-text-color" className="h-9 w-12 rounded border border-slate-200" />
              <input value={field.color || ""} onChange={(e) => onChange({ ...field, color: e.target.value })} className="flex-1 h-9 px-2 rounded-md border border-slate-200 text-[13px] focus:outline-none" />
            </div>
          </div>
        </div>
      )}
      {field.type === "hyperlink" && (
        <div>
          <label className="text-[12px] font-medium text-slate-600">URL</label>
          <input value={field.url || ""} onChange={(e) => onChange({ ...field, url: e.target.value })} data-testid="prop-url" placeholder="https://example.com"
            className="mt-1 w-full h-9 px-3 rounded-md border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
        </div>
      )}
      {showPlaceholder && (
        <div>
          <label className="text-[12px] font-medium text-slate-600">Placeholder</label>
          <input value={field.placeholder} onChange={(e) => onChange({ ...field, placeholder: e.target.value })} data-testid="prop-placeholder"
            className="mt-1 w-full h-9 px-3 rounded-md border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
        </div>
      )}
      {field.type === "dropdown" && (
        <div>
          <label className="text-[12px] font-medium text-slate-600">Options</label>
          <div className="mt-1 space-y-1.5" data-testid="prop-options">
            {(field.options || []).map((o, i) => (
              <div key={i} className="flex gap-1.5">
                <input value={o} onChange={(e) => { const opts = [...field.options]; opts[i] = e.target.value; onChange({ ...field, options: opts }); }} data-testid={`prop-option-${i}`}
                  className="flex-1 h-8 px-2.5 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                <button onClick={() => onChange({ ...field, options: field.options.filter((_, j) => j !== i) })} data-testid={`prop-option-remove-${i}`} className="h-8 w-8 rounded-md text-red-400 hover:bg-red-50 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <button onClick={() => onChange({ ...field, options: [...(field.options || []), `Option ${(field.options?.length || 0) + 1}`] })} data-testid="prop-add-option" className="h-8 px-2.5 rounded-md text-[12px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add option</button>
          </div>
        </div>
      )}
      {!DISPLAY_ONLY_TYPES.has(field.type) && (
        <label className="flex items-center gap-2 text-[13px] text-slate-700">
          <input type="checkbox" checked={!!field.required} onChange={(e) => onChange({ ...field, required: e.target.checked })} data-testid="prop-required" className="accent-blue-600 h-4 w-4" />
          Required field
        </label>
      )}
      {showMap && (
        <div>
          <label className="text-[12px] font-medium text-slate-600">Maps to customer field</label>
          <select value={field.map} onChange={(e) => onChange({ ...field, map: e.target.value })} data-testid="prop-map"
            className="mt-1 w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px] bg-white focus:outline-none">
            {MAP_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      )}
      {FIXED_MAP_TYPES.has(field.type) && field.type !== "heading" && (
        <div className="text-[11.5px] text-slate-400">This element maps automatically to the customer&apos;s {field.type === "fullName" ? "name" : "address"}.</div>
      )}

      {!DISPLAY_ONLY_TYPES.has(field.type) && (
        <div className="pt-3 border-t border-slate-100">
          <label className="flex items-center gap-2 text-[13px] font-medium text-slate-700">
            <input type="checkbox" checked={!!cond} onChange={(e) => toggleCond(e.target.checked)} data-testid="prop-cond-toggle" className="accent-violet-600 h-4 w-4" />
            <span className="flex items-center gap-1"><GitBranch className="h-3.5 w-3.5 text-violet-600" /> Conditional logic</span>
          </label>
          {cond && (
            <div className="mt-2.5 space-y-2 bg-violet-50/60 border border-violet-100 rounded-lg p-3" data-testid="prop-cond-box">
              <div className="text-[11.5px] text-slate-500">Only show this field when…</div>
              {sources.length === 0 ? (
                <div className="text-[12px] text-amber-600">Add another field first to use as a condition.</div>
              ) : (
                <>
                  <select value={cond.fieldId} onChange={(e) => setCond({ fieldId: e.target.value })} data-testid="prop-cond-field"
                    className="w-full h-8 px-2 rounded-md border border-slate-200 text-[12.5px] bg-white focus:outline-none">
                    {sources.map((f) => <option key={f.id} value={f.id}>{f.label || f.type}</option>)}
                  </select>
                  <select value={cond.op} onChange={(e) => setCond({ op: e.target.value })} data-testid="prop-cond-op"
                    className="w-full h-8 px-2 rounded-md border border-slate-200 text-[12.5px] bg-white focus:outline-none">
                    {COND_OPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {needsValue && (
                    srcField?.type === "dropdown" ? (
                      <select value={cond.value || ""} onChange={(e) => setCond({ value: e.target.value })} data-testid="prop-cond-value"
                        className="w-full h-8 px-2 rounded-md border border-slate-200 text-[12.5px] bg-white focus:outline-none">
                        <option value="">Select…</option>
                        {(srcField.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : srcField?.type === "checkbox" ? (
                      <select value={cond.value || ""} onChange={(e) => setCond({ value: e.target.value })} data-testid="prop-cond-value"
                        className="w-full h-8 px-2 rounded-md border border-slate-200 text-[12.5px] bg-white focus:outline-none">
                        <option value="Yes">Checked</option>
                        <option value="No">Unchecked</option>
                      </select>
                    ) : (
                      <input value={cond.value || ""} onChange={(e) => setCond({ value: e.target.value })} data-testid="prop-cond-value" placeholder="value"
                        className="w-full h-8 px-2.5 rounded-md border border-slate-200 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-violet-500/20" />
                    )
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <button onClick={() => onDelete(field.id)} data-testid="prop-delete" className="w-full h-9 rounded-md text-[13px] font-medium text-red-600 border border-red-200 hover:bg-red-50 flex items-center justify-center gap-1.5"><Trash2 className="h-4 w-4" /> Delete field</button>
    </div>
  );
}

function HeaderProperties({ form, set }) {
  return (
    <div className="space-y-4" data-testid="props-header">
      <div className="text-[11px] font-semibold tracking-wide text-slate-400 uppercase">Form header</div>
      <div>
        <label className="text-[12px] font-medium text-slate-600">Heading</label>
        <input value={form.title} onChange={(e) => set({ title: e.target.value })} data-testid="prop-form-title"
          className="mt-1 w-full h-9 px-3 rounded-md border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
      </div>
      <div>
        <label className="text-[12px] font-medium text-slate-600">Intro text</label>
        <textarea value={form.intro} onChange={(e) => set({ intro: e.target.value })} data-testid="prop-form-intro" rows={3}
          className="mt-1 w-full px-3 py-2 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
      </div>
    </div>
  );
}

/* ---------------- Settings & Publish tabs ---------------- */
function SettingsTab({ form, set, onPickLogo, onRemoveLogo, pipelines = [], pipelineStages = [], onRefreshPipelines }) {
  return (
    <div className="max-w-2xl mx-auto py-6 space-y-4" data-testid="builder-settings">
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3.5">
        <div className="text-[13px] font-semibold text-slate-800">Theme</div>
        <div className="grid grid-cols-3 gap-2.5" data-testid="settings-themes">
          {THEMES.map((t) => {
            const active = (form.theme || "classic") === t.id;
            const accent = form.accentColor || "#2563EB";
            return (
              <button key={t.id} onClick={() => set({ theme: t.id })} data-testid={`settings-theme-${t.id}`}
                className={`text-left rounded-lg border overflow-hidden transition-all ${active ? "border-blue-500 ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="h-12 bg-slate-50 relative">
                  {t.id === "bold" ? <div className="h-6" style={{ backgroundColor: accent }} /> : t.id === "classic" ? <div className="h-1.5" style={{ backgroundColor: accent }} /> : null}
                  <div className="px-2 pt-1.5"><div className="h-1.5 w-12 bg-slate-300 rounded" /><div className="h-1.5 w-8 bg-slate-200 rounded mt-1" /></div>
                </div>
                <div className="px-2.5 py-1.5 flex items-center justify-between">
                  <div><div className="text-[12px] font-medium text-slate-700">{t.name}</div><div className="text-[10.5px] text-slate-400">{t.desc}</div></div>
                  {active && <Check className="h-3.5 w-3.5 text-blue-600" />}
                </div>
              </button>
            );
          })}
        </div>
        <div className="pt-1 flex items-center justify-between">
          <div>
            <div className="text-[12px] font-medium text-slate-700">Logo</div>
            <div className="text-[11px] text-slate-400">Shown at the top of your form. Defaults to your company logo. PNG, JPG, WEBP or SVG, up to 5 MB.</div>
          </div>
          <div className="flex items-center gap-2">
            {form.logoDataUrl && <img src={form.logoDataUrl} alt="logo" className="h-8 object-contain" />}
            {form.logoDataUrl ? (
              <button onClick={onRemoveLogo} data-testid="settings-logo-remove" className="h-8 px-2.5 rounded-md text-[12px] font-medium text-red-600 border border-red-200 hover:bg-red-50">Remove</button>
            ) : (
              <button onClick={onPickLogo} data-testid="settings-logo-add" className="h-8 px-2.5 rounded-md text-[12px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1"><ImagePlus className="h-3.5 w-3.5" /> Upload</button>
            )}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3.5">
        <div className="text-[13px] font-semibold text-slate-800">Form details</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-slate-500">Internal name</label>
            <input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="settings-name" className="mt-1 w-full h-9 px-3 rounded-md border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Accent color</label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" value={form.accentColor || "#2563EB"} onChange={(e) => set({ accentColor: e.target.value })} data-testid="settings-accent" className="h-9 w-12 rounded border border-slate-200" />
              <input value={form.accentColor || ""} onChange={(e) => set({ accentColor: e.target.value })} className="flex-1 h-9 px-2 rounded-md border border-slate-200 text-[13px] focus:outline-none" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-slate-500">Submit button label</label>
            <input value={form.submitLabel} onChange={(e) => set({ submitLabel: e.target.value })} data-testid="settings-submit-label" className="mt-1 w-full h-9 px-3 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Success message</label>
            <input value={form.successMessage} onChange={(e) => set({ successMessage: e.target.value })} data-testid="settings-success-msg" className="mt-1 w-full h-9 px-3 rounded-md border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
          </div>
        </div>
        <div>
          <label className="text-[11px] text-slate-500">Input text color</label>
          <p className="text-[10.5px] text-slate-400 mb-1">Color of the text a visitor types into any field (Full Name, Email, Phone, Address, Short/Long Text, Dropdown, Date). Doesn't affect placeholders or labels.</p>
          <div className="mt-1 flex items-center gap-2 max-w-[220px]">
            <input type="color" value={form.inputTextColor || "#0F172A"} onChange={(e) => set({ inputTextColor: e.target.value })} data-testid="settings-input-text-color" className="h-9 w-12 rounded border border-slate-200" />
            <input value={form.inputTextColor || ""} onChange={(e) => set({ inputTextColor: e.target.value })} placeholder="#0F172A" data-testid="settings-input-text-color-hex" className="flex-1 h-9 px-2 rounded-md border border-slate-200 text-[13px] focus:outline-none" />
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-2.5">
        <div className="text-[13px] font-semibold text-slate-800">When someone submits</div>
        <label className="flex items-center gap-2 text-[13px] text-slate-600"><input type="checkbox" checked={!!form.createCustomer} onChange={(e) => set({ createCustomer: e.target.checked })} data-testid="settings-create-customer" className="accent-blue-600 h-4 w-4" /> Add them to my customer database automatically</label>
        <label className="flex items-center gap-2 text-[13px] text-slate-600"><input type="checkbox" checked={!!form.notify} onChange={(e) => set({ notify: e.target.checked })} data-testid="settings-notify" className="accent-blue-600 h-4 w-4" /> Email me a notification</label>
        {form.notify && (
          <input value={form.notifyEmail || ""} onChange={(e) => set({ notifyEmail: e.target.value })} data-testid="settings-notify-email" placeholder="Notify email (defaults to your From address)" className="w-full h-8 px-2.5 rounded-md border border-slate-200 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
        )}
        <div className="pt-1">
          <div className="flex items-center justify-between mb-1">
            <label className="block text-[12.5px] font-medium text-slate-700">Send new leads to a pipeline</label>
            {onRefreshPipelines && (
              <button type="button" onClick={onRefreshPipelines} data-testid="settings-refresh-pipelines"
                className="text-[11px] font-medium text-blue-600 hover:text-blue-700">
                Refresh
              </button>
            )}
          </div>
          <select
            value={form.targetPipelineId || ""}
            onChange={(e) => set({ targetPipelineId: e.target.value, targetStageId: "" })}
            data-testid="settings-target-pipeline"
            className="w-full h-9 px-3 rounded-md border border-slate-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value="">— Do not add to a pipeline —</option>
            {pipelines.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {form.targetPipelineId && (
            <select
              value={form.targetStageId || ""}
              onChange={(e) => set({ targetStageId: e.target.value })}
              data-testid="settings-target-stage"
              className="mt-2 w-full h-9 px-3 rounded-md border border-slate-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="">First stage (default)</option>
              {pipelineStages.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}
          {pipelines.length === 0 && (
            <p className="text-[11px] text-slate-400 mt-1">No custom pipelines yet — create one on the Pipeline page, then click Refresh above.</p>
          )}
        </div>
        <p className="text-[11px] text-slate-400">Every submission is also saved to this form&apos;s Leads inbox.</p>
      </div>
    </div>
  );
}

function PublishTab({ form, onPublish }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const publicUrl = `${origin}/f/${form.id}`;
  const embedCode = `<iframe src="${publicUrl}" width="100%" height="640" style="border:0;max-width:480px" title="${(form.title || "Form").replace(/"/g, "&quot;")}"></iframe>`;
  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };
  const isPlatformContactForm = form.companyId === "__platform__" && form.id === PLATFORM_CONTACT_FORM_ID;
  const [pubState, setPubState] = useState("idle"); // idle | saving | updated
  const handlePublish = async () => {
    setPubState("saving");
    try {
      await onPublish();
      setPubState("updated");
      setTimeout(() => setPubState("idle"), 2000);
    } catch {
      setPubState("idle");
    }
  };
  return (
    <div className="max-w-2xl mx-auto py-6 space-y-4" data-testid="builder-publish">
      {isPlatformContactForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-[13px] font-semibold text-slate-800 mb-1">Service Guru Pro website</div>
          <p className="text-[11.5px] text-slate-400 mb-3">This form powers the public Contact Us page at servicegurupro.com/contact. Save your changes below, then push them live.</p>
          <Button
            onClick={handlePublish}
            disabled={pubState === "saving"}
            data-testid="publish-to-website-btn"
            className={`h-9 gap-1.5 text-white ${pubState === "updated" ? "bg-emerald-600 hover:bg-emerald-600" : "bg-blue-600 hover:bg-blue-700"}`}
          >
            {pubState === "saving" ? <Loader2 className="h-4 w-4 animate-spin" /> : pubState === "updated" ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
            {pubState === "updated" ? "Updated" : "Update Service Guru Website"}
          </Button>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="text-[13px] font-semibold text-slate-800 mb-2">Public link</div>
        <div className="flex gap-2">
          <input readOnly value={publicUrl} data-testid="form-public-url" className="flex-1 h-9 px-3 rounded-md border border-slate-200 bg-slate-50 text-[13px] text-slate-600" />
          <Button variant="outline" onClick={() => copy(publicUrl, "Link")} data-testid="form-copy-url" className="h-9 gap-1.5"><Copy className="h-4 w-4" /> Copy</Button>
          <Button variant="outline" onClick={() => window.open(publicUrl, "_blank")} data-testid="form-open-url" className="h-9 gap-1.5"><ExternalLink className="h-4 w-4" /> Open</Button>
        </div>
        <p className="mt-2 text-[11.5px] text-slate-400">Share in social ads, email signatures, or QR codes. {form.status !== "active" && <span className="text-amber-600">Form is disabled — activate it to accept submissions.</span>}</p>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="text-[13px] font-semibold text-slate-800 mb-2">Embed on your website</div>
        <textarea readOnly value={embedCode} data-testid="form-embed-code" rows={3} className="w-full px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-[12px] font-mono text-slate-600" />
        <Button variant="outline" onClick={() => copy(embedCode, "Embed code")} data-testid="form-copy-embed" className="mt-2 h-9 gap-1.5"><Copy className="h-4 w-4" /> Copy embed code</Button>
      </div>
    </div>
  );
}

/* ---------------- Main full-screen builder ---------------- */
export default function FormBuilder({ formId, open, onClose }) {
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("build");
  const [preview, setPreview] = useState(false);
  const [selectedId, setSelectedId] = useState("header");
  const [saving, setSaving] = useState(false);
  const [dropIndex, setDropIndex] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const dragRef = useRef(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    pipelineApi.pipelines()
      .then(setPipelines)
      .catch((err) => console.warn("Failed to load pipelines:", err?.response?.status, err?.message));
  }, [open]);

  // Also refetch every time the user opens the Settings tab so newly-created
  // pipelines from the Pipeline page appear without needing to reopen the form.
  useEffect(() => {
    if (!open || tab !== "settings") return;
    pipelineApi.pipelines()
      .then(setPipelines)
      .catch((err) => console.warn("Failed to refresh pipelines:", err?.response?.status, err?.message));
  }, [open, tab]);

  const refreshPipelines = useCallback(async () => {
    try {
      const list = await pipelineApi.pipelines();
      setPipelines(list);
      toast.success(`Loaded ${list.length} pipeline${list.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Could not refresh pipelines");
    }
  }, []);

  // Load stages for the form's currently-selected target pipeline.
  const targetPid = form?.targetPipelineId || "";
  useEffect(() => {
    if (!targetPid) return;
    let active = true;
    pipelineApi.stages(targetPid).then((s) => active && setPipelineStages(s)).catch(() => {});
    return () => { active = false; };
  }, [targetPid]);

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be under 5 MB."); e.target.value = ""; return; }
    try {
      const res = await formsApi.uploadLogo(form.id, file);
      setForm((f) => ({ ...f, logoDataUrl: res.logoDataUrl }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload logo.");
    }
    e.target.value = "";
  };

  const removeLogo = async () => {
    try { await formsApi.removeLogo(form.id); } catch (err) { /* clear locally regardless */ }
    setForm((f) => ({ ...f, logoDataUrl: null }));
  };

  const load = useCallback(async () => {
    if (!formId) return;
    const f = await formsApi.get(formId);
    setForm({ ...f, fields: (f.fields || []).map(normalizeField) });
    setSelectedId("header");
    setTab("build");
    setPreview(false);
  }, [formId]);

  useEffect(() => { (async () => { if (open) await load(); })(); }, [open, load]);

  if (!open || !form) return null;

  const fields = form.fields || [];
  const accent = form.accentColor || "#2563EB";
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const insertField = (idx, nf) => setForm((f) => { const arr = [...f.fields]; arr.splice(idx, 0, nf); return { ...f, fields: arr }; });
  const addField = (type) => { const nf = makeField(type); insertField(fields.length, nf); setSelectedId(nf.id); };
  const updateField = (id, next) => setForm((f) => ({ ...f, fields: f.fields.map((x) => (x.id === id ? next : x)) }));
  const deleteField = (id) => { setForm((f) => ({ ...f, fields: f.fields.filter((x) => x.id !== id) })); setSelectedId((s) => (s === id ? "header" : s)); };
  const dupField = (idx) => setForm((f) => { const arr = [...f.fields]; const copy = { ...arr[idx], id: `fld-${Math.random().toString(36).slice(2, 10)}` }; arr.splice(idx + 1, 0, copy); return { ...f, fields: arr }; });
  const moveBy = (idx, dir) => setForm((f) => { const arr = [...f.fields]; const j = idx + dir; if (j < 0 || j >= arr.length) return f; [arr[idx], arr[j]] = [arr[j], arr[idx]]; return { ...f, fields: arr }; });
  const moveTo = (from, to) => setForm((f) => { const arr = [...f.fields]; const [m] = arr.splice(from, 1); let t = to; if (from < to) t = to - 1; arr.splice(t, 0, m); return { ...f, fields: arr }; });

  const onZoneOver = (e, i) => { e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); setDropIndex(e.clientY < r.top + r.height / 2 ? i : i + 1); };
  const handleDrop = (e) => {
    e.preventDefault();
    const d = dragRef.current;
    const di = dropIndex == null ? fields.length : dropIndex;
    if (d?.kind === "new") { const nf = makeField(d.type); insertField(di, nf); setSelectedId(nf.id); }
    else if (d?.kind === "move") { moveTo(d.index, di); }
    dragRef.current = null; setDropIndex(null);
  };
  const onDragEnd = () => { dragRef.current = null; setDropIndex(null); };

  const save = async ({ silent = false, extra = {} } = {}) => {
    if (!fields.length) { if (!silent) toast.error("Add at least one field."); throw new Error("no-fields"); }
    setSaving(true);
    try {
      const patch = {
        name: form.name, title: form.title, intro: form.intro, submitLabel: form.submitLabel,
        successMessage: form.successMessage, accentColor: form.accentColor, inputTextColor: form.inputTextColor || "", theme: form.theme,
        logoDataUrl: form.logoDataUrl || "", fields: form.fields,
        createCustomer: form.createCustomer, notify: form.notify, notifyEmail: form.notifyEmail, status: form.status,
        targetPipelineId: form.targetPipelineId || "", targetStageId: form.targetStageId || "",
        ...extra,
      };
      await formsApi.update(form.id, patch);
      if (extra.status) setForm((f) => ({ ...f, status: extra.status }));
      if (!silent) toast.success("Form saved");
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Failed to save form.");
      throw e;
    } finally { setSaving(false); }
  };

  const publishToWebsite = async () => {
    await save({ silent: true, extra: { status: "active" } });
    toast.success("Service Guru Pro website updated");
  };

  const TABS = [["build", "Build", LayoutGrid], ["settings", "Settings", SettingsIcon], ["publish", "Publish", Share2]];
  const selField = fields.find((x) => x.id === selectedId);

  return (
    <div className="fb-scope fixed inset-0 z-[120] bg-slate-100 flex flex-col" data-testid="form-builder">
      <input type="file" ref={logoInputRef} accept="image/*" onChange={onLogoFile} className="hidden" data-testid="builder-logo-input" />
      {/* Top bar */}
      <div className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onClose} data-testid="builder-close" title="Back" className="h-10 w-10 rounded-md bg-slate-800 hover:bg-slate-900 shadow-sm flex items-center justify-center shrink-0"><ChevronLeft className="h-7 w-7 shrink-0" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} /></button>
          <div className="flex items-center gap-2 min-w-0">
            <Pencil className="h-3.5 w-3.5 text-slate-300 shrink-0" />
            <input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="form-name"
              className="text-[14px] font-semibold text-slate-900 bg-transparent focus:outline-none focus:bg-slate-50 rounded px-1 min-w-0 max-w-[220px]" />
            <button onClick={() => set({ status: form.status === "active" ? "disabled" : "active" })} data-testid="form-status-toggle"
              className={`h-6 px-2 rounded-full text-[10.5px] font-semibold shrink-0 ${form.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{form.status === "active" ? "Active" : "Disabled"}</button>
          </div>
        </div>

        <div className="flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`builder-tab-${k}`}
              className={`h-9 px-3.5 rounded-md text-[13px] font-medium flex items-center gap-1.5 transition-colors ${tab === k ? "bg-blue-50 text-blue-700" : "text-slate-500 hover:bg-slate-100"}`}>
              <Icon className="h-4 w-4" /> {l}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {tab === "build" && (
            <button onClick={() => setPreview((p) => !p)} data-testid="builder-preview-toggle"
              className={`h-9 px-3 rounded-md text-[13px] font-medium flex items-center gap-1.5 transition-colors ${preview ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <Eye className="h-4 w-4" /> Preview
            </button>
          )}
          <Button onClick={save} disabled={saving} data-testid="builder-save" className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex min-h-0">
        {tab === "build" && !preview && <Palette dragRef={dragRef} onAdd={addField} onDragEnd={onDragEnd} />}

        {tab === "build" ? (
          <div className="flex-1 overflow-y-auto" onClick={() => setSelectedId(null)}>
            <div className="max-w-[640px] mx-auto my-8 px-4">
              {preview && <div className="mb-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full"><Eye className="h-3.5 w-3.5" /> Preview mode</div>}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                {(() => { const hs = headerStyle(form.theme, accent); return (
                <div
                  style={hs.wrap}
                  data-testid="canvas-header"
                  onClick={() => !preview && setSelectedId("header")}
                  className={`px-7 pt-6 pb-4 cursor-pointer transition-all ${!preview && selectedId === "header" ? "ring-2 ring-inset ring-blue-300" : ""}`}
                >
                  {form.logoDataUrl ? (
                    <div
                      className="mb-3 group/logo relative inline-flex items-center gap-2"
                      style={{
                        width: "100%",
                        justifyContent: form.logoAlign === "center" ? "center" : form.logoAlign === "right" ? "flex-end" : "flex-start",
                      }}
                      data-testid="canvas-logo-wrap"
                    >
                      <img
                        src={form.logoDataUrl}
                        alt="logo"
                        className="object-contain"
                        style={{ height: `${form.logoSize || 40}px`, maxWidth: "60%" }}
                        data-testid="canvas-logo-img"
                      />
                      {!preview && (
                        <div
                          className="absolute -top-2 opacity-0 group-hover/logo:opacity-100 transition-opacity pointer-events-auto flex items-center gap-1 bg-slate-900 text-white rounded-full shadow-lg px-2 py-1 z-10"
                          style={{
                            left: form.logoAlign === "left" ? "0" : "auto",
                            right: form.logoAlign === "right" ? "0" : "auto",
                            transform: form.logoAlign === "center" ? "translateX(-50%)" : "none",
                            ...(form.logoAlign === "center" ? { left: "50%" } : {}),
                          }}
                          data-testid="canvas-logo-controls"
                        >
                          {[["left", "Left"], ["center", "Center"], ["right", "Right"]].map(([k, lbl]) => (
                            <button
                              key={k}
                              onClick={(e) => { e.stopPropagation(); set({ logoAlign: k }); }}
                              data-testid={`canvas-logo-align-${k}`}
                              className={`text-[11px] font-medium px-2 py-0.5 rounded-full transition-colors ${form.logoAlign === k || (!form.logoAlign && k === "left") ? "bg-blue-500 text-white" : "text-white/80 hover:bg-white/15 hover:text-white"}`}
                              style={{ color: "#ffffff" }}
                            >
                              <span style={{ color: "#ffffff" }}>{lbl}</span>
                            </button>
                          ))}
                          <span className="w-px h-4 bg-white/25 mx-0.5" />
                          <button
                            onClick={(e) => { e.stopPropagation(); set({ logoSize: Math.max(20, (form.logoSize || 40) - 8) }); }}
                            data-testid="canvas-logo-shrink"
                            title="Smaller"
                            className="text-[13px] font-bold w-5 h-5 rounded-full hover:bg-white/15 flex items-center justify-center"
                            style={{ color: "#ffffff" }}
                          >−</button>
                          <button
                            onClick={(e) => { e.stopPropagation(); set({ logoSize: Math.min(120, (form.logoSize || 40) + 8) }); }}
                            data-testid="canvas-logo-grow"
                            title="Larger"
                            className="text-[13px] font-bold w-5 h-5 rounded-full hover:bg-white/15 flex items-center justify-center"
                            style={{ color: "#ffffff" }}
                          >+</button>
                          <span className="w-px h-4 bg-white/25 mx-0.5" />
                          <button
                            onClick={(e) => { e.stopPropagation(); removeLogo(); }}
                            data-testid="canvas-logo-remove"
                            title="Remove logo"
                            className="text-[11px] font-medium px-2 py-0.5 rounded-full hover:bg-rose-500/70"
                            style={{ color: "#ffffff" }}
                          >
                            <span style={{ color: "#ffffff" }}>Remove</span>
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (!preview && (
                    <button onClick={(e) => { e.stopPropagation(); logoInputRef.current?.click(); }} data-testid="canvas-logo-add"
                      className={`mb-3 inline-flex items-center gap-1.5 text-[12px] font-medium border border-dashed rounded-md px-2.5 py-1.5 transition-colors ${form.theme === "bold" ? "border-white/50 text-white/80 hover:border-white" : "border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-600"}`}>
                      <ImagePlus className="h-3.5 w-3.5" /> Add your logo
                    </button>
                  ))}
                  <h1 className={`text-[22px] font-bold ${hs.title}`}>{form.title || "Form"}</h1>
                  {form.intro && <p className={`text-[13.5px] mt-1.5 ${hs.intro}`}>{form.intro}</p>}
                </div>
                ); })()}
                <div className="border-t border-slate-100" />
                {/* Fields */}
                <div className="px-7 py-5" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} data-testid="form-canvas">
                  {fields.length === 0 ? (
                    <div
                      onDragOver={(e) => { e.preventDefault(); setDropIndex(0); }}
                      data-testid="canvas-empty"
                      className="border-2 border-dashed border-slate-300 rounded-xl py-14 flex flex-col items-center justify-center text-center text-slate-400"
                    >
                      <Plus className="h-6 w-6 mb-2" />
                      <div className="text-[13.5px]">Drag your first question here from the left.</div>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {fields.map((f, i) => (
                        <React.Fragment key={f.id}>
                          {!preview && dropIndex === i && <DropBar />}
                          <div onDragOver={preview ? undefined : (e) => onZoneOver(e, i)}>
                            {preview ? (
                              DISPLAY_ONLY_TYPES.has(f.type) ? (
                                <div className="px-4 py-3"><FieldDisplayBlock field={f} accent={accent} /></div>
                              ) : (
                                <div className="px-4 py-3">
                                  <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">{f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}</label>
                                  <FieldControl field={f} disabled accent={accent} inputTextColor={form.inputTextColor} />
                                </div>
                              )
                            ) : (
                              <CanvasField field={f} idx={i} total={fields.length} selected={selectedId === f.id} accent={accent} inputTextColor={form.inputTextColor}
                                onSelect={setSelectedId} onDelete={deleteField} onDup={dupField} onMove={moveBy}
                                onFieldDragStart={(idx) => { dragRef.current = { kind: "move", index: idx }; }} onDragEnd={onDragEnd} />
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                      {!preview && dropIndex === fields.length && <DropBar />}
                      {!preview && <div className="h-5" onDragOver={(e) => { e.preventDefault(); setDropIndex(fields.length); }} />}
                    </div>
                  )}
                </div>
                {/* Submit */}
                <div className="px-7 pb-7 pt-1 flex justify-center">
                  <button style={{ backgroundColor: accent }} className="h-11 px-8 rounded-lg text-white text-[14px] font-semibold cursor-default">{form.submitLabel || "Submit"}</button>
                </div>
              </div>
            </div>
          </div>
        ) : tab === "settings" ? (
          <div className="flex-1 overflow-y-auto px-4"><SettingsTab form={form} set={set} onPickLogo={() => logoInputRef.current?.click()} onRemoveLogo={removeLogo} pipelines={pipelines} pipelineStages={pipelineStages} onRefreshPipelines={refreshPipelines} /></div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4"><PublishTab form={form} onPublish={publishToWebsite} /></div>
        )}

        {/* Right properties panel */}
        {tab === "build" && !preview && (
          <div className="w-80 shrink-0 bg-white border-l border-slate-200 overflow-y-auto p-5" data-testid="props-panel">
            {selectedId === "header" ? (
              <HeaderProperties form={form} set={set} />
            ) : selField ? (
              <FieldProperties field={selField} allFields={fields} onChange={(next) => updateField(selField.id, next)} onDelete={deleteField} />
            ) : (
              <div className="text-[13px] text-slate-400 flex flex-col items-center text-center pt-10" data-testid="props-empty">
                <LayoutGrid className="h-7 w-7 text-slate-300 mb-2" />
                Select a field to edit its settings, or drag an element from the left to add it.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
